import { useEffect, useState } from "react";
import { Search, SlidersHorizontal, Users, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/Common";
import { CandidateCard } from "@/components/CandidateCard";
import { api, useShortlist } from "@/lib/api";

const EMPTY = { q: "", category: "all", availability: "all", status: "all", min_rate: "", max_rate: "", min_exp: "all" };

const FSelect = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] font-mono uppercase tracking-wider text-stone-500">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="bg-white"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Any</SelectItem>
        {options.map((o) => <SelectItem key={o.v ?? o} value={String(o.v ?? o)}>{o.l ?? o}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export default function Candidates() {
  const [f, setF] = useState(EMPTY);
  const [list, setList] = useState(null);
  const [meta, setMeta] = useState({ categories: [] });
  const { ids, toggle } = useShortlist();
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { api.get("/candidates/meta").then((r) => setMeta(r.data)); }, []);
  useEffect(() => {
    const t = setTimeout(async () => {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v !== "all"));
      const { data } = await api.get("/candidates", { params });
      setList(data);
    }, 250);
    return () => clearTimeout(t);
  }, [f]);

  const active = Object.entries(f).some(([k, v]) => v !== EMPTY[k]);

  return (
    <div>
      <PageHeader eyebrow="Talent directory" title="Find your virtual worker" desc="Every candidate has been interviewed and skills-tested by our recruitment team." />
      <div className="grid gap-8 xl:grid-cols-[280px_1fr]">
        <aside className="h-fit rounded-md border border-stone-200 bg-[#F5F5F0] p-6 xl:sticky xl:top-8" data-testid="candidate-filters">
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2 font-display font-bold text-stone-900"><SlidersHorizontal className="h-4 w-4" /> Filters</p>
            {active && <button data-testid="filters-clear-button" onClick={() => setF(EMPTY)} className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900"><X className="h-3 w-3" />Clear</button>}
          </div>
          <div className="mt-5 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
              <Input data-testid="candidate-search-input" value={f.q} onChange={(e) => set("q")(e.target.value)} placeholder="Name, skill, tool…" className="bg-white pl-9" />
            </div>
            <FSelect label="Role category" value={f.category} onChange={set("category")} options={meta.categories} testId="filter-category" />
            <FSelect label="Availability" value={f.availability} onChange={set("availability")} options={["Full-time", "Part-time"]} testId="filter-availability" />
            <FSelect label="Status" value={f.status} onChange={set("status")} options={["Available", "Interviewing", "Placed"]} testId="filter-status" />
            <FSelect label="Min. experience" value={f.min_exp} onChange={set("min_exp")} options={[{ v: 2, l: "2+ years" }, { v: 5, l: "5+ years" }, { v: 8, l: "8+ years" }]} testId="filter-experience" />
            <div className="space-y-1.5">
              <Label className="text-[11px] font-mono uppercase tracking-wider text-stone-500">Hourly rate (USD)</Label>
              <div className="flex gap-2">
                <Input data-testid="filter-min-rate" type="number" placeholder="Min" value={f.min_rate} onChange={(e) => set("min_rate")(e.target.value)} className="bg-white" />
                <Input data-testid="filter-max-rate" type="number" placeholder="Max" value={f.max_rate} onChange={(e) => set("max_rate")(e.target.value)} className="bg-white" />
              </div>
            </div>
          </div>
        </aside>
        <section>
          <p className="mb-4 text-sm text-stone-500" data-testid="candidate-count">{list ? `${list.length} candidate${list.length === 1 ? "" : "s"}` : "Loading…"}</p>
          {list && list.length === 0 ? (
            <EmptyState testId="candidates-empty" icon={Users} title="No candidates match" desc="Try widening your filters, or message our team and we'll source someone for you." />
          ) : (
            <div className="grid gap-6 md:grid-cols-2 2xl:grid-cols-3">
              {(list || []).map((c) => <CandidateCard key={c.id} c={c} shortlisted={ids.includes(c.id)} onToggle={toggle} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
